import React from 'react';
import styled from 'styled-components';
import { ReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import { SingleValueProps, components, OptionProps } from 'react-select';
import useGenericReactSelectOption from '../../../utils/hooks/useGenericReactSelectOption';
import RenamedIndicator from '../../RenamedIndicator';
import { BasicDropdownIndicator } from '../../Dropdown/StyledReactSelect/SelectUtils';
import { IReactSelectOption } from '../../../utils/types';
import { ITooltipOption } from '../../KubitTooltip/globalTooltips';
import KubitTooltip from '../../KubitTooltip';
import { theme } from '../../../styles/theme';

interface IProps<T> {
  singleValueLabel: React.ReactNode;
  options: T[];
  selectedKey: string;
  properties: [keyof T, keyof T];
  onChange: (option: T | null) => void;
  tooltipOption: ITooltipOption | null;
  isDisabled?: boolean;
}

interface IChartGroupDropdownValueProps {
  label: string;
  rank: number | null;
  hasUserAlias?: boolean;
}

const GROUP_MAX_WIDTH = '320px';
const DropdownOptionContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const Rank = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  font-weight: 600;
`;
const DropdownLabelName = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export default <T extends object>({
  options,
  selectedKey,
  singleValueLabel,
  properties,
  onChange,
  tooltipOption,
  isDisabled,
}: IProps<T>) => {
  const ChartGroupDropdownValue = ({
    label,
    rank,
    hasUserAlias,
  }: IChartGroupDropdownValueProps) => (
    <DropdownOptionContainer>
      {typeof rank === 'number' && <Rank>{`${rank}.`}</Rank>}
      <DropdownLabelName>{label}</DropdownLabelName>
      {hasUserAlias && <RenamedIndicator />}
    </DropdownOptionContainer>
  );

  const { formattedOptions, formattedSelectedOption } =
    useGenericReactSelectOption({
      options,
      selectedItemKey: selectedKey,
      properties,
    });

  const renderSingleValueLabel = (
    props: SingleValueProps<T & IReactSelectOption, false>,
  ) => {
    const popupProps = tooltipOption ?? {};
    return (
      <components.SingleValue {...props}>
        <KubitTooltip
          disabled={!tooltipOption}
          {...popupProps}
          noOpenDelay={true}
        >
          {singleValueLabel}
        </KubitTooltip>
      </components.SingleValue>
    );
  };

  const renderOptionItem = (
    props: OptionProps<T & IReactSelectOption, false>,
  ) => {
    // hacked, change if retention measure works with <T>
    const { data } = props as any;
    return (
      <components.Option {...props}>
        <ChartGroupDropdownValue
          label={data.label}
          rank={data.rank ?? null}
          hasUserAlias={data.hasUserAlias}
        />
      </components.Option>
    );
  };

  const availableOptions = formattedOptions.filter(
    (option) => option.value !== selectedKey,
  );

  return (
    <ReactSelectMenu
      menuPortalTarget={document.body}
      options={formattedOptions}
      onChange={onChange}
      isSearchable={false}
      isClearable={false}
      value={formattedSelectedOption}
      isMulti={false}
      openMenuOnClick={false}
      components={{
        SingleValue: renderSingleValueLabel,
        DropdownIndicator: (props) =>
          availableOptions.length > 0
            ? BasicDropdownIndicator({
                props,
                dropdownArrowSize: 16,
                color: isDisabled
                  ? theme.colors.secondary400
                  : theme.colors.secondary600,
              })
            : null,
        Option: renderOptionItem,
      }}
      hideSelectedOptions
      closeMenuOnSelect
      menuPosition="fixed"
      styles={{
        control: (base) => ({
          ...base,
          maxWidth: GROUP_MAX_WIDTH,
          width: 'fit-content',
          minWidth: '20px',
          height: '24px',
          minHeight: '24px',
          borderColor: 'transparent',
          flexWrap: 'nowrap',
          backgroundColor: 'transparent',
          border: 'none',
        }),
        valueContainer: (base) => ({
          ...base,
          padding: '0px',
        }),
        menu: (base) => ({
          ...base,
          maxWidth: GROUP_MAX_WIDTH,
          width: '-webkit-fill-available',
          minWidth: '200px',
        }),
        menuList: (base) => ({
          ...base,
          maxWidth: GROUP_MAX_WIDTH,
          width: '-webkit-fill-available',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          cursor: 'pointer',
        }),
      }}
    />
  );
};
